import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import datingIllustration from '../assets/images/5.png';
import ProposalForm from "./ProposalForm";

const BRAND_BLUE = "#0047ff";
const BRAND_GRADIENT = "linear-gradient(135deg, #0047ff, #00c4ff)";

const Wrapper = styled.div`
  font-family: 'Poppins', sans-serif;
  min-height: 100vh;
  color: #e5e7eb;
  padding: 80px 20px;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  position: relative;
`;

const Hero = styled(motion.section)`
  text-align: center;
  padding: 40px 20px;

  @media (max-width: 360px) {
    padding: 30px 14px;  
  }
`;

const HeroTitle = styled(motion.h1)`
  max-width: 860px;
  margin: 0 auto 22px;
  text-align: center;
  font-size: clamp(2.1rem, 5vw, 4rem);
  line-height: 1.15;
  font-weight: 700;
  color: white;
  letter-spacing: -1px;
`;

const HeroText = styled.p`
  max-width: 800px;
  margin: 0 auto;
  font-size: 1.1rem;
  line-height: 1.7;
  color: #cbd5e1;
`;

const CtaButton = styled(motion.button)`
  margin-top: 30px;
  padding: 14px 36px;
  border-radius: 50px;
  border: none;
  background: ${BRAND_GRADIENT};
  color: white;
  font-weight: 600;
  font-size: 1.05rem;
  cursor: pointer;
`;

const WhySection = styled.section`
  display: flex;
  flex-direction: column;   
  align-items: center;
  text-align: center;
  gap: 24px;
  max-width: 900px;
  width: 100%;
  margin: 60px auto;
  padding: 0 16px;
`;

const WhyText = styled.div`
  max-width: 700px;
`;

const WhyTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 16px;
  background: ${BRAND_GRADIENT};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const WhyPara = styled.p`
  font-size: 1.05rem;
  line-height: 1.7;
  color: #cbd5e1;
`;

const WhyImage = styled.img`
  width: 100%;
  max-width: 320px;
  height: auto;
  border-radius: 20px;
  display: block;
  margin: 0 auto;
`;

const FeaturesGrid = styled.section`
  display: flex;
  flex-wrap: wrap;
  gap: 30px;
  justify-content: center;
  padding-top: 60px;
`;

const FeatureBox = styled(motion.div)`
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(14px);
  border-radius: 18px;
  padding: 26px;
  max-width: 320px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0,0,0,0.25);
  border: 1px solid rgba(255,255,255,0.12);
`;

const FeatureTitle = styled.h3`
  font-size: 1.25rem;
  margin-bottom: 10px;
  background: ${BRAND_GRADIENT};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const FeatureDesc = styled.p`
  font-size: 0.95rem;
  line-height: 1.6;
  color: #cbd5e1;
`;

export default function Android() {

  const [showProposal, setShowProposal] = useState(false);

  return (
    <>
      <Wrapper>
        <Hero
          initial={{ opacity: 0, y: -40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: 'easeOut' }}
        >
          <HeroTitle>Custom Android App Development Company</HeroTitle>
          <HeroText>
            Elevate your business with our custom Android app development services. Our expert team crafts secure,
            feature-rich solutions, leveraging the latest tech. Maximize brand loyalty with intuitive and cost-efficient
            apps. Solve global business challenges with our robust Android solutions. Partner with us for a digital
            transformation that puts innovation in the palm of your users' hands.
          </HeroText>

          <CtaButton
            whileHover={{ scale: 1.08, boxShadow: '0 0 16px rgba(0,86,210,.3)' }}
            whileTap={{ scale: 0.96 }}
            transition={{ type: 'spring', stiffness: 260, damping: 18 }}
            onClick={() => setShowProposal(true)}
          >
            Request a Proposal
          </CtaButton>
        </Hero>

        <WhySection>
          <WhyText>
            <WhyTitle>Why Develop An Android App?</WhyTitle>
            <WhyPara>
              Android, commanding a market share of 71.65% in Q3 2023, has emerged as the dominant force in the mobile market.
              With over 86% market share in application development, Android offers unparalleled reach. At Berrybloomer, our Android
              expertise enables us to craft tailored apps that align seamlessly with your needs.
            </WhyPara>
          </WhyText>

          <WhyImage
            src={datingIllustration}
            alt="Android App Illustration"
          />
        </WhySection>

        <FeaturesGrid>
          {features.map((feature) => (
            <FeatureBox
              key={feature.title}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              whileHover={{
                y: -12,
                scale: 1.05,
                boxShadow: "0 0 35px rgba(0,196,255,0.35)",
              }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            >
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDesc>{feature.description}</FeatureDesc>
            </FeatureBox>
          ))}
        </FeaturesGrid>
      </Wrapper>

      <ProposalForm open={showProposal} setOpen={setShowProposal} />
    </>
  );
}

const features = [
  {
    title: 'Android Application Consultation',
    description:
      'Our team will craft the best-quality Android app solutions so you can achieve your goals faster with a customer-centric approach.',
  },
  {
    title: 'Android App Support & Maintenance',
    description:
      'Say goodbye to unexpected crashes and glitches. We ensure flawless performance, functionality, and user happiness.',
  },
  {
    title: 'Hybrid Mobile App Development',
    description:
      'We craft cost-effective hybrid apps capable of running on Android, offering aesthetic appeal and seamless functionality.',
  },
  {
    title: 'Custom Android App Development',
    description:
      'We build custom Android apps that align with your business goals, handling everything from concept to deployment.',
  },
  {
    title: 'App Modernization',
    description:
      'We help update and align your existing Android apps with the latest industry standards and user expectations.',
  },
  {
    title: 'Android UI/UX Design',
    description:
      'Our design team creates stunning and intuitive interfaces for all Android devices and screen sizes.',
  },
];